<template>
  <transition name="inactivity-modal">
    <div v-if="$inactivity.store.showWarning" class="inactivity-overlay" @click.self="extendSession">
      <div class="inactivity-modal" role="dialog" aria-labelledby="inactivity-title" aria-describedby="inactivity-desc">
        <div class="inactivity-modal__accent" />

        <div class="inactivity-modal__header">
          <div class="inactivity-modal__icon-wrap">
            <ClockIcon class="inactivity-modal__icon" />
          </div>
          <div class="inactivity-modal__header-text">
            <h2 id="inactivity-title" class="inactivity-modal__title">Session en pause</h2>
            <p id="inactivity-desc" class="inactivity-modal__subtitle">Vous êtes inactif depuis un moment</p>
          </div>
        </div>

        <div class="inactivity-modal__body">
          <p class="inactivity-modal__message">
            Aucune activité détectée depuis un moment. Sans action, votre session sera fermée dans&nbsp;:
          </p>
          <div class="inactivity-modal__countdown">
            <div class="inactivity-modal__timer-wrap">
              <svg class="inactivity-modal__timer-svg" viewBox="0 0 120 120">
                <circle class="inactivity-modal__timer-bg" cx="60" cy="60" r="54" fill="none" stroke-width="6" />
                <circle
                  class="inactivity-modal__timer-fill"
                  cx="60"
                  cy="60"
                  r="54"
                  fill="none"
                  stroke-width="6"
                  stroke-linecap="round"
                  stroke-dasharray="339"
                  :stroke-dashoffset="timerStrokeOffset"
                />
              </svg>
              <span class="inactivity-modal__timer-value">{{ formatTime($inactivity.store.secondsRemaining) }}</span>
            </div>
            <p class="inactivity-modal__hint">Cliquez sur « Rester connecté » pour continuer</p>
          </div>
        </div>

        <div class="inactivity-modal__actions">
          <button type="button" class="inactivity-modal__btn inactivity-modal__btn--primary" @click="extendSession">
            <CheckIcon class="inactivity-modal__btn-icon" />
            Rester connecté
          </button>
          <button type="button" class="inactivity-modal__btn inactivity-modal__btn--secondary" @click="logoutManual">
            <ArrowRightOnRectangleIcon class="inactivity-modal__btn-icon" />
            Me déconnecter
          </button>
        </div>

        <div class="inactivity-modal__footer">
          <p class="inactivity-modal__footer-text">
            <span class="inactivity-modal__footer-label">Sécurité</span>
            La déconnexion automatique protège votre compte en cas d’oubli.
          </p>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ClockIcon, CheckIcon, ArrowRightOnRectangleIcon } from '@heroicons/vue/24/outline';
import { INACTIVITY_GRACE_PERIOD_SEC } from '@/utils/inactivityConfig';

export default {
  name: 'InactivityWarning',
  methods: {
    extendSession() {
      this.$inactivity.extendSession();
    },
    logoutManual() {
      this.$inactivity.logoutManual();
    },
    formatTime(seconds) {
      const s = Math.max(0, Math.floor(seconds));
      const minutes = Math.floor(s / 60);
      const secs = s % 60;
      return `${minutes}:${secs.toString().padStart(2, '0')}`;
    },
  },
  computed: {
    timerStrokeOffset() {
      const remaining = this.$inactivity?.store?.secondsRemaining ?? 0;
      const pct = Math.max(0, Math.min(1, remaining / INACTIVITY_GRACE_PERIOD_SEC));
      const circumference = 2 * Math.PI * 54;
      return circumference * (1 - pct);
    },
  },
};
</script>

<style scoped>
.inactivity-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
}

.inactivity-modal {
  position: relative;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25), 0 0 0 1px rgba(15, 23, 42, 0.05);
  max-width: 420px;
  width: 100%;
  overflow: hidden;
  font-family: var(--font-primary);
}

.inactivity-modal__accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #d89c24 0%, #b8821a 100%);
}

.inactivity-modal__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem 1.5rem 0;
}

.inactivity-modal__icon-wrap {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(212, 160, 23, 0.15) 0%, rgba(184, 134, 11, 0.1) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.inactivity-modal__icon {
  width: 28px;
  height: 28px;
  color: #b8821a;
}

.inactivity-modal__header-text {
  min-width: 0;
}

.inactivity-modal__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  letter-spacing: -0.02em;
}

.inactivity-modal__subtitle {
  margin: 0.25rem 0 0;
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.inactivity-modal__body {
  padding: 1.5rem 1.5rem 0;
}

.inactivity-modal__message {
  margin: 0 0 1.5rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #475569;
}

.inactivity-modal__countdown {
  text-align: center;
}

.inactivity-modal__timer-wrap {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inactivity-modal__timer-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.inactivity-modal__timer-bg {
  stroke: #e2e8f0;
}

.inactivity-modal__timer-fill {
  stroke: #d89c24;
  transition: stroke-dashoffset 1s linear;
}

.inactivity-modal__timer-value {
  position: relative;
  z-index: 1;
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.inactivity-modal__hint {
  margin: 0;
  font-size: 0.8125rem;
  color: #64748b;
}

.inactivity-modal__actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.5rem 1.5rem 0;
}

.inactivity-modal__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.875rem 1.25rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.inactivity-modal__btn:active {
  transform: scale(0.98);
}

.inactivity-modal__btn--primary {
  background: linear-gradient(135deg, #d89c24 0%, #b8821a 100%);
  color: #fff;
  box-shadow: 0 2px 8px rgba(212, 160, 23, 0.35);
}

.inactivity-modal__btn--primary:hover {
  box-shadow: 0 4px 16px rgba(212, 160, 23, 0.4);
}

.inactivity-modal__btn--secondary {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.inactivity-modal__btn--secondary:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.inactivity-modal__btn-icon {
  width: 1.25rem;
  height: 1.25rem;
}

.inactivity-modal__footer {
  padding: 1rem 1.5rem 1.25rem;
  background: #f8fafc;
  border-top: 1px solid #f1f5f9;
}

.inactivity-modal__footer-text {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #64748b;
}

.inactivity-modal__footer-label {
  font-weight: 600;
  color: #475569;
  margin-right: 0.25rem;
}

/* Animations */
.inactivity-modal-enter-active,
.inactivity-modal-leave-active {
  transition: opacity 0.25s ease;
}
.inactivity-modal-enter-active .inactivity-modal,
.inactivity-modal-leave-active .inactivity-modal {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.inactivity-modal-enter-from,
.inactivity-modal-leave-to {
  opacity: 0;
}
.inactivity-modal-enter-from .inactivity-modal,
.inactivity-modal-leave-to .inactivity-modal {
  transform: scale(0.96) translateY(8px);
}

@media (max-width: 480px) {
  .inactivity-modal__title {
    font-size: 1.125rem;
  }
  .inactivity-modal__timer-value {
    font-size: 1.75rem;
  }
}
</style>
